import { useState, useEffect, useCallback, useRef } from 'react';
import {
    deleteNotification,
    getNotificationsRequestWL,
} from '../../services/notificationService';

/**
 * Hook publico de notificaciones.
 *
 * Compatibilidad hacia atras: `useUserNotifications()` sigue devolviendo
 * `{ data, loading, error }` (el shape que ya consume HeaderNotificationsMenu),
 * pero ahora ademas expone `deleteById(id)` para borrar un item y `refetch()`
 * para forzar recarga desde la nueva pantalla /notifications.
 *
 * El delete es optimista: quita el item del state local ANTES de esperar la
 * respuesta del backend. Si el servidor rechaza (403/404/500) restauramos el
 * state y propagamos el error al consumidor. Esto evita que el usuario vea la
 * lista "congelada" mientras dura el round-trip.
 */
export const useUserNotifications = (): {
    data: any | null;
    loading: boolean;
    error: Error | null;
    deleteById: (id: number) => Promise<void>;
    refetch: () => Promise<void>;
} => {
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const isMountedRef = useRef<boolean>(true);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const fresh = await getNotificationsRequestWL();
            if (isMountedRef.current) setData(fresh);
        } catch (err) {
            if (isMountedRef.current) setError(err as Error);
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        fetchData();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchData]);

    const deleteById = useCallback(async (id: number) => {
        // Rollback si el backend rechaza (IDOR o notif ajena).
        const previous = data;
        setData((prev: any) =>
            Array.isArray(prev) ? prev.filter((n: any) => n.id !== id) : prev
        );

        try {
            await deleteNotification(id);
        } catch (err) {
            if (isMountedRef.current) setData(previous);
            throw err;
        }
    }, [data]);

    return { data, loading, error, deleteById, refetch: fetchData };
};
